import {
  createOffsetPaginatedSchema,
  CroDtoSchema,
  type CreateCroRequest,
  type CreateSponsorRequest,
  type CreatePersonnelDetailsRequest,
  type CreateTrialPersonnelRequest,
  type CreateTrialRequest,
  type CreateTrialRoleRequest,
  type CroDto,
  type CroListQuery,
  type OffsetPaginated,
  PersonnelDetailsDtoSchema,
  type PersonnelDetailsDto,
  type PersonnelListQuery,
  SponsorDtoSchema,
  type SponsorDto,
  type SponsorListQuery,
  TrialDtoSchema,
  type TrialDto,
  type TrialListQuery,
  type TrialParticipantDto,
  TrialParticipantsDtoSchema,
  type TrialParticipantsQuery,
  TrialPersonnelDtoSchema,
  type TrialPersonnelDto,
  TrialRoleDtoSchema,
  type TrialRoleDto,
  TrialVisitDtoSchema,
  type TrialVisitDto,
  BulkCreateTrialVisitsResponseSchema,
  type BulkCreateTrialVisitsRequest,
  type BulkCreateTrialVisitsResponse,
  BulkUpdateTrialVisitsResponseSchema,
  type BulkUpdateTrialVisitsRequest,
  type BulkUpdateTrialVisitsResponse,
  TrialVisitGroupDtoSchema,
  type TrialVisitGroupDto,
  type CreateTrialVisitGroupRequest,
  type UpdateTrialVisitGroupRequest,
  type CreateTrialVisitRequest,
  type CreateTrialVisitWindowModeRequest,
  type UpdatePersonnelDetailsRequest,
  type UpdateTrialPersonnelRequest,
  type UpdateTrialRequest,
  type UpdateTrialRoleRequest,
  type UpdateTrialVisitRequest,
  type ReorderTrialVisitRequest,
  type ReorderTrialVisitsRequest,
  type UpdateTrialVisitWindowModeRequest,
  TrialVisitWindowModeDtoSchema,
  type TrialVisitWindowModeDto,
  type AddCriterionAnswerRequest,
  type CreateCriterionRequest,
  type UpdateCriterionRequest,
  TrialCriteriaConfigDtoSchema,
  type TrialCriteriaConfigDto,
  TrialCriterionAnswerDtoSchema,
  type TrialCriterionAnswerDto,
  TrialCriterionDtoSchema,
  type TrialCriterionDto,
} from '@ctms/contracts';

import { z } from 'zod';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

const TrialVisitListSchema = z.array(TrialVisitDtoSchema);
const TrialVisitGroupListSchema = z.array(TrialVisitGroupDtoSchema);
const TrialVisitWindowModeListSchema = z.array(TrialVisitWindowModeDtoSchema);
const TrialRoleListSchema = z.array(TrialRoleDtoSchema);
const TrialPersonnelListSchema = z.array(TrialPersonnelDtoSchema);

const TrialListResponseSchema = createOffsetPaginatedSchema(TrialDtoSchema);
const SponsorListResponseSchema = createOffsetPaginatedSchema(SponsorDtoSchema);
const CroListResponseSchema = createOffsetPaginatedSchema(CroDtoSchema);
const PersonnelListResponseSchema = createOffsetPaginatedSchema(PersonnelDetailsDtoSchema);

function appendListParams(
  searchParams: URLSearchParams,
  query: { page: number; limit: number; search?: string; status?: string; sort?: string },
): void {
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.search && query.search.trim().length > 0) {
    searchParams.set('search', query.search.trim());
  }
  // Filters stay off the wire when unset — the API rejects empty status values.
  if (query.status !== undefined && query.status.length > 0) {
    searchParams.set('status', query.status);
  }
  // The sort key travels only when the menu picked one (tor C).
  if (query.sort !== undefined && query.sort.length > 0) {
    searchParams.set('sort', query.sort);
  }
}

export const trialsApi = {
  async listTrials(query: TrialListQuery): Promise<OffsetPaginated<TrialDto>> {
    const searchParams = new URLSearchParams();
    appendListParams(searchParams, query);
    if (query.active !== undefined) {
      searchParams.set('active', String(query.active));
    }

    const raw: unknown = await apiClient.get(API_PATHS.trials.list, { searchParams }).json();
    return TrialListResponseSchema.parse(raw);
  },

  async createTrial(request: CreateTrialRequest): Promise<TrialDto> {
    const raw: unknown = await apiClient.post(API_PATHS.trials.create, { json: request }).json();
    return TrialDtoSchema.parse(raw);
  },

  async getTrial(id: number): Promise<TrialDto> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.byId(id)).json();
    return TrialDtoSchema.parse(raw);
  },

  async updateTrial(id: number, request: UpdateTrialRequest): Promise<TrialDto> {
    const raw: unknown = await apiClient.patch(API_PATHS.trials.byId(id), { json: request }).json();
    return TrialDtoSchema.parse(raw);
  },

  async listVisits(trialId: number): Promise<TrialVisitDto[]> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.visits(trialId)).json();
    return TrialVisitListSchema.parse(raw);
  },

  /**
   * Participants of one trial (element 5 + filter 11 of the Cockpit legend).
   * `search` and `status` travel only when set, mirroring `listTrials`.
   */
  async listEnrollments(
    trialId: number,
    query: TrialParticipantsQuery,
  ): Promise<OffsetPaginated<TrialParticipantDto>> {
    const searchParams = new URLSearchParams();
    appendListParams(searchParams, query);
    const raw: unknown = await apiClient
      .get(API_PATHS.trials.enrollments(trialId), { searchParams })
      .json();
    return TrialParticipantsDtoSchema.parse(raw);
  },

  async createVisit(trialId: number, request: CreateTrialVisitRequest): Promise<TrialVisitDto> {
    const raw: unknown = await apiClient.post(API_PATHS.trials.visits(trialId), { json: request }).json();
    return TrialVisitDtoSchema.parse(raw);
  },

  /** D.4 (mail 6): edit of one catalog visit after it was saved. */
  async updateVisit(
    trialId: number,
    visitId: number,
    request: UpdateTrialVisitRequest,
  ): Promise<TrialVisitDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.visitById(trialId, visitId), { json: request })
      .json();
    return TrialVisitDtoSchema.parse(raw);
  },

  /** #44 pkt 6: trwała kolejność katalogu — swap visit_number z sąsiadem. */
  async reorderVisit(
    trialId: number,
    visitId: number,
    request: ReorderTrialVisitRequest,
  ): Promise<TrialVisitDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.visitReorder(trialId, visitId), { json: request })
      .json();
    return TrialVisitDtoSchema.parse(raw);
  },

  /** #44 pkt 6 (round 2): move a contiguous block of selected visits as one unit. */
  async reorderVisits(
    trialId: number,
    request: ReorderTrialVisitsRequest,
  ): Promise<TrialVisitDto[]> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.visitsReorder(trialId), { json: request })
      .json();
    return TrialVisitListSchema.parse(raw);
  },

  async listWindowModes(trialId: number, visitId: number): Promise<TrialVisitWindowModeDto[]> {
    const raw: unknown = await apiClient
      .get(API_PATHS.trials.windowModes(trialId, visitId))
      .json();
    return TrialVisitWindowModeListSchema.parse(raw);
  },

  async createWindowMode(
    trialId: number,
    visitId: number,
    request: CreateTrialVisitWindowModeRequest,
  ): Promise<TrialVisitWindowModeDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.windowModes(trialId, visitId), { json: request })
      .json();
    return TrialVisitWindowModeDtoSchema.parse(raw);
  },

  async updateWindowMode(
    trialId: number,
    visitId: number,
    modeId: number,
    request: UpdateTrialVisitWindowModeRequest,
  ): Promise<TrialVisitWindowModeDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.windowModeById(trialId, visitId, modeId), { json: request })
      .json();
    return TrialVisitWindowModeDtoSchema.parse(raw);
  },

  async listRoles(trialId: number): Promise<TrialRoleDto[]> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.roles(trialId)).json();
    return TrialRoleListSchema.parse(raw);
  },

  async createRole(trialId: number, request: CreateTrialRoleRequest): Promise<TrialRoleDto> {
    const raw: unknown = await apiClient.post(API_PATHS.trials.roles(trialId), { json: request }).json();
    return TrialRoleDtoSchema.parse(raw);
  },

  async updateRole(
    trialId: number,
    roleId: number,
    request: UpdateTrialRoleRequest,
  ): Promise<TrialRoleDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.roleById(trialId, roleId), { json: request })
      .json();
    return TrialRoleDtoSchema.parse(raw);
  },

  async deleteRole(trialId: number, roleId: number): Promise<void> {
    await apiClient.delete(API_PATHS.trials.roleById(trialId, roleId));
  },

  async listTrialPersonnel(trialId: number): Promise<TrialPersonnelDto[]> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.personnel(trialId)).json();
    return TrialPersonnelListSchema.parse(raw);
  },

  async createTrialPersonnel(
    trialId: number,
    request: CreateTrialPersonnelRequest,
  ): Promise<TrialPersonnelDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.personnel(trialId), { json: request })
      .json();
    return TrialPersonnelDtoSchema.parse(raw);
  },

  async updateTrialPersonnel(
    trialId: number,
    assignmentId: number,
    request: UpdateTrialPersonnelRequest,
  ): Promise<TrialPersonnelDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.personnelById(trialId, assignmentId), { json: request })
      .json();
    return TrialPersonnelDtoSchema.parse(raw);
  },

  async deleteTrialPersonnel(trialId: number, assignmentId: number): Promise<void> {
    await apiClient.delete(API_PATHS.trials.personnelById(trialId, assignmentId));
  },

  async listPersonnel(query: PersonnelListQuery): Promise<OffsetPaginated<PersonnelDetailsDto>> {
    const searchParams = new URLSearchParams();
    appendListParams(searchParams, query);
    if (query.internalPersonnel !== undefined) {
      searchParams.set('internalPersonnel', String(query.internalPersonnel));
    }
    const raw: unknown = await apiClient.get(API_PATHS.personnel.list, { searchParams }).json();
    return PersonnelListResponseSchema.parse(raw);
  },

  async createPersonnel(request: CreatePersonnelDetailsRequest): Promise<PersonnelDetailsDto> {
    const raw: unknown = await apiClient.post(API_PATHS.personnel.list, { json: request }).json();
    return PersonnelDetailsDtoSchema.parse(raw);
  },

  async getPersonnel(id: number): Promise<PersonnelDetailsDto> {
    const raw: unknown = await apiClient.get(API_PATHS.personnel.byId(id)).json();
    return PersonnelDetailsDtoSchema.parse(raw);
  },

  async updatePersonnel(id: number, request: UpdatePersonnelDetailsRequest): Promise<PersonnelDetailsDto> {
    const raw: unknown = await apiClient.patch(API_PATHS.personnel.byId(id), { json: request }).json();
    return PersonnelDetailsDtoSchema.parse(raw);
  },

  async listSponsors(query: SponsorListQuery): Promise<OffsetPaginated<SponsorDto>> {
    const searchParams = new URLSearchParams();
    appendListParams(searchParams, query);
    const raw: unknown = await apiClient
      .get(API_PATHS.dictionaries.sponsors, { searchParams })
      .json();
    return SponsorListResponseSchema.parse(raw);
  },

  async createSponsor(request: CreateSponsorRequest): Promise<SponsorDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.dictionaries.sponsors, { json: request })
      .json();
    return SponsorDtoSchema.parse(raw);
  },

  async listCros(query: CroListQuery): Promise<OffsetPaginated<CroDto>> {
    const searchParams = new URLSearchParams();
    appendListParams(searchParams, query);
    const raw: unknown = await apiClient.get(API_PATHS.dictionaries.cros, { searchParams }).json();
    return CroListResponseSchema.parse(raw);
  },

  async createCro(request: CreateCroRequest): Promise<CroDto> {
    const raw: unknown = await apiClient.post(API_PATHS.dictionaries.cros, { json: request }).json();
    return CroDtoSchema.parse(raw);
  },

  // ─── Qualification criteria configuration (REQ-PAT-018…020, issue #30) ───
  // Trial-anchored paths, Domain 3 payload — the same controller placement as
  // the participant list. The criteria tab shows the whole configuration in
  // one round trip (no pagination: a protocol criterion list is short).

  async listCriteria(trialId: number): Promise<TrialCriteriaConfigDto> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.criteria(trialId)).json();
    return TrialCriteriaConfigDtoSchema.parse(raw);
  },

  async createCriterion(
    trialId: number,
    request: CreateCriterionRequest,
  ): Promise<TrialCriterionDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.criteria(trialId), { json: request })
      .json();
    return TrialCriterionDtoSchema.parse(raw);
  },

  async updateCriterion(
    trialId: number,
    criteriaId: number,
    request: UpdateCriterionRequest,
  ): Promise<TrialCriterionDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.criterionById(trialId, criteriaId), { json: request })
      .json();
    return TrialCriterionDtoSchema.parse(raw);
  },

  /** Gate 6: with saved patient answers the API answers 409 and the row stays. */
  async deleteCriterion(trialId: number, criteriaId: number): Promise<void> {
    await apiClient.delete(API_PATHS.trials.criterionById(trialId, criteriaId));
  },

  async addCriterionAnswer(
    trialId: number,
    criteriaId: number,
    request: AddCriterionAnswerRequest,
  ): Promise<TrialCriterionAnswerDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.criterionAnswers(trialId, criteriaId), { json: request })
      .json();
    return TrialCriterionAnswerDtoSchema.parse(raw);
  },

  /** Saved patient answers are text copies (REQ-PAT-023) — a removal never touches them. */
  async deleteCriterionAnswer(trialId: number, criteriaId: number, answerId: number): Promise<void> {
    await apiClient.delete(API_PATHS.trials.criterionAnswerById(trialId, criteriaId, answerId));
  },

  // ─── Visit groups + bulk creation (D2, #40) ───
  // The „++" wizard generates names client-side and posts the final list;
  // `visitNumber` is ALWAYS server-assigned (REQ-008: max+1, sequential
  // within the bulk).

  async listVisitGroups(trialId: number): Promise<TrialVisitGroupDto[]> {
    const raw: unknown = await apiClient.get(API_PATHS.trials.visitGroups(trialId)).json();
    return TrialVisitGroupListSchema.parse(raw);
  },

  async createVisitGroup(
    trialId: number,
    request: CreateTrialVisitGroupRequest,
  ): Promise<TrialVisitGroupDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.visitGroups(trialId), { json: request })
      .json();
    return TrialVisitGroupDtoSchema.parse(raw);
  },

  async updateVisitGroup(
    trialId: number,
    groupId: number,
    request: UpdateTrialVisitGroupRequest,
  ): Promise<TrialVisitGroupDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.visitGroupById(trialId, groupId), { json: request })
      .json();
    return TrialVisitGroupDtoSchema.parse(raw);
  },

  /**
   * Decyzja #40-A4 (2): NO cascade — the API answers 409
   * `TRIAL_VISIT_GROUP_IN_USE` while visits are still attached.
   */
  async deleteVisitGroup(trialId: number, groupId: number): Promise<void> {
    await apiClient.delete(API_PATHS.trials.visitGroupById(trialId, groupId));
  },

  async bulkCreateVisits(
    trialId: number,
    request: BulkCreateTrialVisitsRequest,
  ): Promise<BulkCreateTrialVisitsResponse> {
    const raw: unknown = await apiClient
      .post(API_PATHS.trials.visitsBulk(trialId), { json: request })
      .json();
    return BulkCreateTrialVisitsResponseSchema.parse(raw);
  },

  /** #44 pkt 5: bulk feature update for selected catalog visits. */
  async bulkUpdateVisits(
    trialId: number,
    request: BulkUpdateTrialVisitsRequest,
  ): Promise<BulkUpdateTrialVisitsResponse> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.trials.visitsBulk(trialId), { json: request })
      .json();
    return BulkUpdateTrialVisitsResponseSchema.parse(raw);
  },
};
